import { escapeHtml } from './helpers';

/**
 * Client-side inspiration card renderer for Elastic listing results.
 * Canonical PHP markup lives in template-parts/cards/card-inspiration.php (SSR).
 */
export const getInspirationGalleryItemLayout = index => {
    const group = index % 5;
    let imgClass = 'w-full object-cover rounded-small rounded-small inspiration-object-focal';
    let colClass = 'desktop:col-span-6';

    switch (group) {
        case 0:
            imgClass += ' desktop:aspect-[5/4]';
            colClass = 'desktop:col-span-7';
            break;
        case 1:
            imgClass += ' desktop:aspect-[16/9]';
            colClass = 'desktop:col-span-5';
            break;
        case 2:
            imgClass += ' desktop:aspect-[4/5]';
            colClass = 'desktop:col-span-4';
            break;
        case 3:
            imgClass += ' desktop:aspect-[5/4]';
            colClass = 'desktop:col-span-3';
            break;
        case 4:
            imgClass += ' desktop:aspect-[4/5]';
            colClass = 'desktop:col-span-5';
            break;
        default:
            break;
    }

    return { colClass, imgClass };
};

const normalizeFocalCoordinate = value => {
    const parsed = Number(value);

    if (!Number.isFinite(parsed)) {
        return 50;
    }

    return Math.max(0, Math.min(100, Math.round(parsed)));
};

export const resolveInspirationFocalStyleFromEntry = (entry = {}, gridIndex = 0) => {
    const focal = entry?.focal && typeof entry.focal === 'object' ? entry.focal : {};
    const slot = gridIndex % 5;
    const x = normalizeFocalCoordinate(
        focal[`slot_${slot}_x`] ?? focal.default_x ?? 50
    );
    const y = normalizeFocalCoordinate(
        focal[`slot_${slot}_y`] ?? focal.default_y ?? 50
    );

    return `--inspiration-focal: ${x}% ${y}%;`;
};

export const resolveInspirationFocalEntry = (item = {}) => {
    const inspiration = item?.inspiration || {};

    if (inspiration?.focal && typeof inspiration.focal === 'object') {
        return { focal: inspiration.focal };
    }

    const thumbnail = inspiration.thumbnail || {};
    const gallery = Array.isArray(inspiration.gallery) ? inspiration.gallery : [];
    const attachmentId = Number(item.attachment_id || 0);

    if (
        Number(thumbnail.attachment_id || 0) === attachmentId &&
        thumbnail.focal
    ) {
        return thumbnail;
    }

    const galleryMatch = gallery.find(
        entry => Number(entry?.attachment_id || 0) === attachmentId && entry?.focal
    );

    if (galleryMatch) {
        return galleryMatch;
    }

    if (thumbnail.focal) {
        return thumbnail;
    }

    return {};
};

export const resolveInspirationDefaultFocalStyleFromEntry = (entry = {}) => {
    const focal = entry?.focal && typeof entry.focal === 'object' ? entry.focal : {};
    const x = normalizeFocalCoordinate(focal.default_x ?? 50);
    const y = normalizeFocalCoordinate(focal.default_y ?? 50);

    return `--inspiration-focal: ${x}% ${y}%;`;
};

export const resolveInspirationItemImage = (item = {}) => {
    const attachmentId = Number(item.attachment_id || 0);
    const inspiration = item.inspiration || {};
    const thumbnail = inspiration.thumbnail || {};
    const gallery = Array.isArray(inspiration.gallery) ? inspiration.gallery : [];

    if (
        Number(thumbnail.attachment_id || 0) === attachmentId &&
        thumbnail.url
    ) {
        return {
            url: String(thumbnail.url),
            mobileUrl: String(thumbnail.mobile_url || ''),
            alt: String(thumbnail.alt || inspiration.name || ''),
        };
    }

    const galleryMatch = gallery.find(
        entry => Number(entry?.attachment_id || 0) === attachmentId && entry?.url
    );

    if (galleryMatch) {
        return {
            url: String(galleryMatch.url),
            mobileUrl: String(galleryMatch.mobile_url || ''),
            alt: String(galleryMatch.alt || inspiration.name || ''),
        };
    }

    if (thumbnail.url) {
        return {
            url: String(thumbnail.url),
            mobileUrl: String(thumbnail.mobile_url || ''),
            alt: String(thumbnail.alt || inspiration.name || ''),
        };
    }

    return { url: '', mobileUrl: '', alt: '' };
};

export const renderInspirationCardFromElastic = (item, options = {}) => {
    const termId = Number(item?.term_id || 0);
    const attachmentId = Number(item?.attachment_id || 0);
    const gridIndex = Number(item?.grid_index || 0);
    const id = `${termId}:${attachmentId}`;
    const openLabel = escapeHtml(
        options.openLabel || 'Zobacz inspirację'
    );

    if (termId <= 0 || attachmentId <= 0) {
        return { id, html: '' };
    }

    const { colClass, imgClass } = getInspirationGalleryItemLayout(gridIndex);
    const { url, mobileUrl, alt } = resolveInspirationItemImage(item);
    const focalStyle = resolveInspirationFocalStyleFromEntry(
        resolveInspirationFocalEntry(item),
        gridIndex
    );
    const mobileSource =
        mobileUrl !== ''
            ? `<source srcset="${escapeHtml(mobileUrl)}" media="(max-width: 767.98px)">`
            : '';
    const imageMarkup =
        url !== ''
            ? `<div class="hover-scale-image__media"><picture>${mobileSource}<img class="${imgClass}" style="${escapeHtml(focalStyle)}" src="${escapeHtml(url)}" alt="${escapeHtml(alt)}"></picture></div>`
            : '';

    const html = `<div class="col-span-2 ${colClass} group relative cursor-pointer hover-scale-image-on-group" data-term-id="${termId}" data-attachment-id="${attachmentId}" data-active="1" js-gallery-item js-inspiration-card>${imageMarkup}<button type="button" class="absolute top-16 right-16 z-10 flex h-[40px]! w-[41.6px]! items-center justify-center rounded-full bg-orange-500 text-grey-900 transition-opacity desktop:opacity-0 desktop:group-hover:opacity-100" data-inspiration-open aria-label="${openLabel}"><i class="icon icon-arrow-right text-[18px]!" aria-hidden="true"></i></button></div>`;

    return { id, html };
};
